import React, { useState } from 'react';
import computeBendingStress from '../utility/calculate_bending_stress';
import computeBendRadiusFromKpsi from '../utility/calculate_bend_radius';
import { kpsiToGPa, gpaToKpsi } from '../utility/convert_stress';
import  { ROUNDING_VALUES } from '../constants/calculator_constants';

import NumberField from './number-field';

const FiberBendingCalculator: React.FC = () => {
  const [claddingDiameter, setCladdingDiameter] = useState<number | string>(125);
  const [bendRadius, setBendRadius] = useState<number | string>(25.7);
  const [stressKpsi, setStressKpsi] = useState<number | string>(25);
  const [stressGPa, setStressGPa] = useState<number | string>(0.172);
  
  // Error states
  const [errors, setErrors] = useState({
    claddingDiameter: '',
    bendRadius: '',
    stressKpsi: '',
    stressGPa: '',
  });

  const validateInput = (value: string | number, fieldName: string, min: number = 0, max: number = Infinity): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    
    if (isNaN(numValue)) {
      return `${fieldName} must be a valid number`;
    }
    
    if (numValue < min) {
      return `${fieldName} must be greater than or equal to ${min}`;
    }
    
    if (numValue > max) {
      return `${fieldName} must be less than or equal to ${max}`;
    }
    
    return '';
  };

  const updateError = (field: string, error: string) => {
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const getCladdingDiameter = (): number => {
    const value = typeof claddingDiameter === 'number' ? claddingDiameter : parseFloat(claddingDiameter);
    return isNaN(value) ? 125 : value;
  };

  const setFieldsToZero = () => {
    setBendRadius(0);
    setStressKpsi(0);
    setStressGPa(0);
  }

  const handleCladdingDiameterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const error = validateInput(value, 'Cladding Diameter', 0, 10000000);
    updateError('claddingDiameter', error);
    
    const v = parseFloat(value);
    if (!isNaN(v) && error === '') {
      setCladdingDiameter(v);
      setFieldsToZero();
    }
    else{
        setCladdingDiameter(value);
        setFieldsToZero();
    }
  }

  const handleBendRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const error = validateInput(value, 'Bend Radius', 0, 10000000);
    updateError('bendRadius', error);
    
    const v = parseFloat(value);
    if (!isNaN(v) && error === '') {
      setBendRadius(v);
      const fibercladdingDiameter = getCladdingDiameter();
      setCladdingDiameter(fibercladdingDiameter);
      const { rawKpsi, rawgpa } = computeBendingStress(Number(v), fibercladdingDiameter);
      const kpsi = Number(rawKpsi?.toFixed(ROUNDING_VALUES.STRESS_KPSI));
      const gpa = Number(rawgpa?.toFixed(ROUNDING_VALUES.STRESS_GPA));

      setStressKpsi(isNaN(kpsi) ? 0 : kpsi);
      setStressGPa(isNaN(gpa) ? 0 : gpa);
    }
    else{
        setBendRadius(value);
        setStressKpsi(0);
        setStressGPa(0);
    }
  };

  const handleKpsiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const error = validateInput(value, 'Stress (kpsi)', 0, 10000000);
    updateError('stressKpsi', error);
    
    const v = parseFloat(value);
    setStressKpsi(value);
    if (!isNaN(v) && error === '') {
      const gpaValue = kpsiToGPa(v);
      setStressGPa(isNaN(gpaValue) ? 0 : Number(gpaValue.toFixed(ROUNDING_VALUES.STRESS_GPA)));
      const fibercladdingDiameter = getCladdingDiameter();
      setCladdingDiameter(fibercladdingDiameter);
      const br = computeBendRadiusFromKpsi(v, fibercladdingDiameter);
      setBendRadius(isNaN(br) ? 0 :Number(br.toFixed(ROUNDING_VALUES.BENDING_RADIUS)));
    }
    else{
        setBendRadius(0);
        setStressGPa(0);
    }
  };

  const handleGPaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const error = validateInput(value, 'Stress (GPa)', 0, 10000000);
    updateError('stressGPa', error);
    
    const v = parseFloat(value);
    setStressGPa(value);
    if (!isNaN(v) && error === '') {
      const kpsi = gpaToKpsi(v);
      setStressKpsi(isNaN(kpsi) ? 0 : Number(kpsi.toFixed(ROUNDING_VALUES.STRESS_KPSI)));
      const fibercladdingDiameter = getCladdingDiameter();
      setCladdingDiameter(fibercladdingDiameter);
      const br = computeBendRadiusFromKpsi(kpsi, fibercladdingDiameter);
      setBendRadius(isNaN(br) ? 0 :Number(br.toFixed(ROUNDING_VALUES.BENDING_RADIUS)));
    }
    else{
        setBendRadius(0);
        setStressKpsi(0);
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-8 md:gap-10 px-4 py-8 sm:p-8 bg-[#f2f4f7] rounded-xl" aria-label="Fiber bending calculator">
      {/* Left column - Description */}
      <div className="w-full">
        <h3 className="mb-5 font-theSans text-[1.3125rem] leading-[120%] tracking-0 text-black">Fiber in bending conversion calculator</h3>
        <p className="text-[1rem] text-[#101114] leading-[150%]">This calculator can be used to estimate the maximum stress associated with fiber in bending. It can also be used to determine the minimum bend radius value necessary to remain below a given stress level. The calculator updates the field values after each <em>keystroke</em>.</p>
      </div>
      <div className="w-full">
        <form 
          onSubmit={(e) => e.preventDefault()}
          className="w-full"
        >
          <fieldset className="flex flex-col gap-6">
            <legend className="sr-only">Fiber in bending conversion calculator</legend>
            {/* Cladding Diameter */}
            <NumberField
              label="Cladding Diameter"
              value={claddingDiameter}
              onChange={handleCladdingDiameterChange}
              unit="µm"
              title="Enter the fiber cladding diameter in micrometers"
              errors={errors?.claddingDiameter}
            />

            {/* Bend Radius */}
            <NumberField
              label="Bend Radius"
              value={bendRadius}
              onChange={handleBendRadiusChange}
              unit="mm"
              step="0.1"
              title="Enter the bend radius in millimeters, or this will be calculated based on stress values"
              errors={errors?.bendRadius}
            />

            {/* Stress (Kpsi) */}
            <NumberField
              label="Stress"
              value={stressKpsi}
              onChange={handleKpsiChange}
              unit="kpsi"
              title="Enter stress in kilopounds per square inch, or this will be calculated"
              errors={errors?.stressKpsi}
            />

            {/* Stress (GPa) */}
            <NumberField
              label="Stress"
              value={stressGPa}
              onChange={handleGPaChange}
              unit="GPa"
              step="0.001"
              title="Enter stress in gigapascals, or this will be calculated"
              errors={errors?.stressKpsi}
            />
          </fieldset>
        </form>
      </div>
    </div>
  );
};

export default FiberBendingCalculator;
